import {
  daysUntil,
  deriveNextPaymentDate,
  getPeriodStart,
} from "./billingSchedule";
import { toISODateString } from "./dateUtils";
import { type DatabaseCategory } from "@/hooks/useCategories";
import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import {
  type PaymentHistory,
  type LifecycleEvent,
  type SubscriptionPeriod,
} from "@/repositories/interfaces";
import { type Subscription } from "@/types/subscription";

/**
 * Calculates the appropriate reconciliation status based on subscription state and payment history
 */
export const calculateReconciliationStatus = (
  dbSubscription: DatabaseSubscription,
  paymentHistory: PaymentHistory[],
): Subscription["reconciliationStatus"] => {
  // Cancelled subscriptions don't need reconciliation
  if (dbSubscription.status === "cancelled") {
    return "cancelled";
  }

  // Trial subscriptions: check if trial has ended
  if (dbSubscription.status === "trial") {
    const trialEndDate = dbSubscription.trial_end_date
      ? new Date(dbSubscription.trial_end_date)
      : null;
    const now = new Date();

    // If trial hasn't ended, no reconciliation needed
    if (!trialEndDate || trialEndDate > now) {
      return "paid";
    }

    // Trial has ended - check if there are recent payments (within last 30 days)
    const recentPayments = paymentHistory.filter(
      (p) =>
        new Date(p.payment_date) >=
          new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000) &&
        p.status === "paid",
    );

    return recentPayments.length > 0 ? "paid" : "pending";
  }

  // Active subscriptions: check payment history for current period
  if (dbSubscription.status === "active") {
    // Unscheduled PAYG (no fixed schedule) → exclude from reconciliation alerts
    if (!dbSubscription.billing_cycle || !dbSubscription.next_payment_date) {
      return "paid";
    }
    const nextPaymentDate = deriveNextPaymentDate({
      billingCycle: dbSubscription.billing_cycle,
      storedNextPaymentDate: dbSubscription.next_payment_date,
      paidDates: paymentHistory
        .filter((p) => p.status === "paid")
        .map((p) => p.payment_date),
    });
    if (!nextPaymentDate) {
      return "paid";
    }

    // Calculate period start using centralized helper
    let frequency:
      | "weekly"
      | "fortnightly"
      | "monthly"
      | "every_2_months"
      | "quarterly"
      | "yearly"
      | "custom" = "monthly";
    let customFrequencyDays: number | undefined;

    if (dbSubscription.billing_cycle.startsWith("custom_")) {
      frequency = "custom";
      customFrequencyDays =
        parseInt(dbSubscription.billing_cycle.split("_")[1]) || 30;
    } else {
      frequency = dbSubscription.billing_cycle as
        | "weekly"
        | "fortnightly"
        | "monthly"
        | "every_2_months"
        | "quarterly"
        | "yearly";
    }

    if (daysUntil(nextPaymentDate) < 0) {
      return "pending";
    }

    const periodStart = getPeriodStart(
      nextPaymentDate,
      frequency,
      customFrequencyDays,
    );

    // Check if there are paid payments in the current period
    const currentPeriodPayments = paymentHistory.filter((p) => {
      const paymentDate = new Date(p.payment_date);
      return (
        paymentDate >= periodStart &&
        paymentDate < nextPaymentDate &&
        p.status === "paid"
      );
    });

    return currentPeriodPayments.length > 0 ? "paid" : "pending";
  }

  // Paused subscriptions don't need reconciliation
  if (dbSubscription.status === "paused") {
    return "paid";
  }

  // Default to paid for any other status
  return "paid";
};

// Transform localStorage Subscription to DatabaseSubscription for Supabase
export const transformToDatabase = (
  subscription: Omit<Subscription, "id" | "createdAt" | "updatedAt">,
): Omit<
  DatabaseSubscription,
  "id" | "user_id" | "created_at" | "updated_at"
> => {
  // Defensive helpers to handle Date | string | undefined
  const toIsoDate = (value: any) => {
    return toISODateString(value);
  };

  const nextPaymentIso = toIsoDate(subscription.nextPayment);
  // Prefer an explicit trialEndDate if provided; otherwise fall back to nextPayment when status === 'trial'
  const trialEndIso = subscription.trialEndDate
    ? toIsoDate(subscription.trialEndDate)
    : subscription.status === "trial"
      ? toIsoDate(subscription.nextPayment)
      : undefined;
  const cancellationIso = toIsoDate(subscription.cancellationDate);
  const startDateIso =
    toIsoDate(subscription.startDate) ?? toIsoDate(new Date());

  return {
    name: subscription.name,
    amount: subscription.amount,
    currency: subscription.currency,
    billing_cycle:
      subscription.frequency === "custom"
        ? `custom_${subscription.customFrequencyDays || 30}`
        : subscription.frequency || null,
    category_id: undefined, // Will need to be mapped separately
    next_payment_date: nextPaymentIso ?? null,
    status: subscription.status,
    notes: subscription.notes,
    provider: undefined,
    website_url: undefined,
    auto_renew: true,
    next_payment_source: subscription.nextPaymentSource || "manual",
    trial_end_date: trialEndIso,
    cancellation_date: cancellationIso,
    rating: subscription.sentiment,
    start_date: startDateIso,
  };
};

// Transform DatabaseSubscription from Supabase to display format
export const transformFromDatabase = (
  dbSubscription: DatabaseSubscription,
  categories: DatabaseCategory[] = [],
  paymentHistory: PaymentHistory[] = [],
  lifecycleEvents: LifecycleEvent[] = [],
  subscriptionPeriods: SubscriptionPeriod[] = [],
): Subscription => {
  const category = categories.find(
    (cat) => cat.id === dbSubscription.category_id,
  );

  // Parse billing cycle
  let frequency: Subscription["frequency"] | undefined = undefined;
  let customFrequencyDays: number | undefined;

  if (dbSubscription.billing_cycle?.startsWith("custom_")) {
    frequency = "custom";
    customFrequencyDays =
      parseInt(dbSubscription.billing_cycle.split("_")[1]) || 30;
  } else if (dbSubscription.billing_cycle) {
    frequency = dbSubscription.billing_cycle as Subscription["frequency"];
  }

  return {
    id: dbSubscription.id,
    name: dbSubscription.name,
    amount: dbSubscription.amount,
    currency: dbSubscription.currency,
    frequency,
    customFrequencyDays,
    nextPayment:
      dbSubscription.status === "trial"
        ? dbSubscription.next_payment_date
          ? new Date(dbSubscription.next_payment_date)
          : null
        : !dbSubscription.next_payment_date &&
            !paymentHistory.some(
              (p) =>
                p.subscription_id === dbSubscription.id && p.status === "paid",
            )
          ? null
          : deriveNextPaymentDate({
              billingCycle: dbSubscription.billing_cycle,
              storedNextPaymentDate: dbSubscription.next_payment_date,
              paidDates: paymentHistory
                .filter((p) => p.subscription_id === dbSubscription.id)
                .filter((p) => p.status === "paid")
                .map((p) => p.payment_date),
            }),
    startDate: new Date(dbSubscription.start_date || dbSubscription.created_at), // Fallback to created_at if start_date not set
    status: dbSubscription.status as Subscription["status"],
    // Map billing type from DB
    billingType: (dbSubscription.billing_type as any) || undefined,
    category: category?.name || "Entertainment",
    reconciliationStatus: calculateReconciliationStatus(
      dbSubscription,
      paymentHistory,
    ),
    nextPaymentSource:
      (dbSubscription.next_payment_source as Subscription["nextPaymentSource"]) ||
      "manual",
    paymentHistory: paymentHistory
      .filter((p) => p.subscription_id === dbSubscription.id)
      .map((p) => ({
        id: p.id,
        subscriptionId: p.subscription_id,
        date: new Date(p.payment_date),
        amount: p.amount,
        currency: p.currency,
        status: p.status,
        reconciliationSource: p.reconciliation_source,
        notes: p.notes,
        createdAt: new Date(p.created_at),
        updatedAt: new Date(p.created_at),
      })),
    lifecycleEvents: lifecycleEvents
      .filter((e) => e.subscription_id === dbSubscription.id)
      .map((e) => ({
        id: e.id,
        subscriptionId: e.subscription_id,
        event: e.event,
        date: new Date(e.event_date),
        reason: e.reason,
        notes: e.notes,
        createdAt: new Date(e.created_at),
      })),
    subscriptionPeriods: subscriptionPeriods
      .filter((p) => p.subscription_id === dbSubscription.id)
      .map((p) => ({
        id: p.id,
        subscriptionId: p.subscription_id,
        startDate: new Date(p.start_date),
        endDate: p.end_date ? new Date(p.end_date) : undefined,
        totalPayments: p.total_payments,
        totalAmount: p.total_amount,
        averageMonthlyAmount: p.average_monthly_amount,
        status: p.status,
        createdAt: new Date(p.created_at),
        updatedAt: new Date(p.updated_at),
      })),
    createdAt: new Date(dbSubscription.created_at),
    updatedAt: new Date(dbSubscription.updated_at),
    cancellationDate: dbSubscription.cancellation_date
      ? new Date(dbSubscription.cancellation_date)
      : undefined,
    // Map trial end date from DB into app-level subscription
    trialEndDate: dbSubscription.trial_end_date
      ? new Date(dbSubscription.trial_end_date)
      : undefined,
    // Map optional richer DB fields back into the app Subscription shape
    paymentMethod: dbSubscription.payment_method || undefined,
    provider: dbSubscription.provider || undefined,
    websiteUrl: dbSubscription.website_url || undefined,
    autoRenew:
      typeof dbSubscription.auto_renew === "boolean"
        ? dbSubscription.auto_renew
        : true,
    sentiment: dbSubscription.rating || 3,
    notes: dbSubscription.notes,
    tags: [],
    reconciliationSnoozedUntil: dbSubscription.reconciliation_snoozed_until
      ? new Date(dbSubscription.reconciliation_snoozed_until)
      : null,
  };
};

/**
 * Lightweight transformation from DatabaseSubscription to Subscription
 * Used for editing scenarios where we don't have full related data loaded yet
 */
export const databaseSubscriptionToSubscription = (
  dbSubscription: DatabaseSubscription,
): Subscription => {
  // Parse billing cycle
  let frequency: Subscription["frequency"] | undefined = undefined;
  let customFrequencyDays: number | undefined;

  if (dbSubscription.billing_cycle?.startsWith("custom_")) {
    frequency = "custom";
    customFrequencyDays =
      parseInt(dbSubscription.billing_cycle.split("_")[1]) || 30;
  } else if (dbSubscription.billing_cycle) {
    frequency = dbSubscription.billing_cycle as Subscription["frequency"];
  }

  return {
    id: dbSubscription.id,
    name: dbSubscription.name,
    amount: dbSubscription.amount,
    currency: dbSubscription.currency,
    frequency,
    customFrequencyDays,
    nextPayment: dbSubscription.next_payment_date
      ? new Date(dbSubscription.next_payment_date)
      : null,
    startDate: new Date(dbSubscription.start_date || dbSubscription.created_at),
    status: dbSubscription.status as Subscription["status"],
    billingType: (dbSubscription.billing_type as any) || "recurring",
    category: undefined,
    reconciliationStatus: "paid", // Default for editing scenarios
    nextPaymentSource:
      (dbSubscription.next_payment_source as Subscription["nextPaymentSource"]) ||
      "manual",
    paymentHistory: [],
    lifecycleEvents: [],
    subscriptionPeriods: [],
    createdAt: new Date(dbSubscription.created_at),
    updatedAt: new Date(dbSubscription.updated_at),
    cancellationDate: dbSubscription.cancellation_date
      ? new Date(dbSubscription.cancellation_date)
      : undefined,
    trialEndDate: dbSubscription.trial_end_date
      ? new Date(dbSubscription.trial_end_date)
      : undefined,
    pauseDate: dbSubscription.pause_date
      ? new Date(dbSubscription.pause_date)
      : undefined,
    resumeDate: dbSubscription.resume_date
      ? new Date(dbSubscription.resume_date)
      : undefined,
    paymentMethod: dbSubscription.payment_method || undefined,
    provider: dbSubscription.provider || undefined,
    websiteUrl: dbSubscription.website_url || undefined,
    autoRenew:
      typeof dbSubscription.auto_renew === "boolean"
        ? dbSubscription.auto_renew
        : true,
    autoRenewalDate: dbSubscription.auto_renewal_date
      ? new Date(dbSubscription.auto_renewal_date)
      : undefined,
    sentiment: dbSubscription.rating || 3,
    notes: dbSubscription.notes,
    tags: [],
    reconciliationSnoozedUntil: dbSubscription.reconciliation_snoozed_until
      ? new Date(dbSubscription.reconciliation_snoozed_until)
      : null,
  };
};

// Transform payment data
export const transformPaymentToDatabase = (
  payment: Omit<PaymentHistory, "id" | "created_at">,
  subscriptionId: string,
): Omit<PaymentHistory, "id" | "created_at"> => {
  return {
    subscription_id: subscriptionId,
    payment_date: payment.payment_date,
    amount: payment.amount,
    currency: payment.currency,
    status: payment.status,
    reconciliation_source: payment.reconciliation_source,
    notes: payment.notes,
  };
};

// Legacy exports for Settings page compatibility
export const transformToSupabaseSubscription = (
  subscription: any,
  categoryMapping: any,
) => {
  const dbData = transformToDatabase(subscription);
  return { ...dbData, category_id: categoryMapping.get(subscription.category) };
};
export const transformToSupabaseCategory = (category: {
  name: string;
  color: string;
}) => ({
  name: category.name,
  color: category.color,
});
export const transformToSupabasePaymentHistory = (
  payment: any,
  subscriptionMapping: any,
) => ({
  subscription_id: subscriptionMapping.get(payment.subscriptionId),
  payment_date: toISODateString(payment.date),
  amount: payment.amount,
  currency: payment.currency,
  status: payment.status || "paid",
  reconciliation_source: payment.reconciliationSource || "manual",
  notes: payment.notes || null,
});
export const defaultSupabaseCategories = [
  { name: "Entertainment", color: "#8B5CF6" },
  { name: "Productivity", color: "#10B981" },
  { name: "Business", color: "#3B82F6" },
  { name: "Education", color: "#F59E0B" },
  { name: "Health & Fitness", color: "#EF4444" },
  { name: "News & Media", color: "#6366F1" },
  { name: "Security", color: "#14B8A6" },
  { name: "Food & Drink", color: "#F97316" },
  { name: "Transportation", color: "#84CC16" },
  { name: "Professional", color: "#8B5CF6" },
];
